
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card.jsx";
import { Button } from "../ui/button.jsx";
import { ScrollArea } from "../ui/scroll-area.jsx";
import { Database, Copy, Check, Download } from 'lucide-react';

export default function PostgresSchema() {
  const [copied, setCopied] = useState(false);
  
  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(sqlScript);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  
  const handleDownload = () => {
    const element = document.createElement('a');
    const file = new Blob([sqlScript], {type: 'text/plain'});
    element.href = URL.createObjectURL(file);
    element.download = 'helpdesk_pro_schema.sql';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const sqlScript = `-- PostgreSQL Database Script for HelpDesk Pro
-- Instruções:
-- 1. Primeiro, crie o banco de dados:
--    CREATE DATABASE helpdesk_pro;
-- 2. Depois, conecte ao banco de dados:
--    \\c helpdesk_pro
-- 3. Por fim, execute este script

-- Criar extensões necessárias
CREATE EXTENSION IF NOT EXISTS "uuid-ossp";
CREATE EXTENSION IF NOT EXISTS "pgcrypto";

-- Criar tipos ENUM
DROP TYPE IF EXISTS user_role CASCADE;
DROP TYPE IF EXISTS user_status CASCADE;
DROP TYPE IF EXISTS company_status CASCADE;
DROP TYPE IF EXISTS ticket_status CASCADE;
DROP TYPE IF EXISTS ticket_priority CASCADE;
DROP TYPE IF EXISTS ticket_type CASCADE;
DROP TYPE IF EXISTS system_category CASCADE;
DROP TYPE IF EXISTS payment_method CASCADE;
DROP TYPE IF EXISTS payment_status CASCADE;
DROP TYPE IF EXISTS invoice_status CASCADE;
DROP TYPE IF EXISTS project_status CASCADE;
DROP TYPE IF EXISTS remote_access_type CASCADE;
DROP TYPE IF EXISTS version_status CASCADE;
DROP TYPE IF EXISTS chat_message_type CASCADE;
DROP TYPE IF EXISTS chat_message_status CASCADE;

CREATE TYPE user_role AS ENUM ('admin', 'manager', 'agent', 'client');
CREATE TYPE user_status AS ENUM ('active', 'inactive', 'pending');
CREATE TYPE company_status AS ENUM ('active', 'inactive');
CREATE TYPE ticket_status AS ENUM ('open', 'analysis', 'in_progress', 'resolved', 'closed', 'reopened');
CREATE TYPE ticket_priority AS ENUM ('low', 'medium', 'high', 'critical');
CREATE TYPE ticket_type AS ENUM ('support', 'development', 'project');
CREATE TYPE system_category AS ENUM ('erp', 'crm', 'ecommerce', 'financeiro', 'rh', 'custom', 'other');
CREATE TYPE payment_method AS ENUM ('pix', 'boleto', 'credit_card');
CREATE TYPE payment_status AS ENUM ('pending', 'processing', 'confirmed', 'failed', 'refunded');
CREATE TYPE invoice_status AS ENUM ('pending', 'paid', 'overdue', 'cancelled');
CREATE TYPE project_status AS ENUM ('planning', 'in_progress', 'paused', 'completed');
CREATE TYPE remote_access_type AS ENUM ('teamviewer', 'anydesk', 'rdp', 'vpn', 'ssh', 'other');
CREATE TYPE version_status AS ENUM ('alpha', 'beta', 'release_candidate', 'stable', 'deprecated', 'discontinued');
CREATE TYPE chat_message_type AS ENUM ('text', 'file', 'audio', 'system');
CREATE TYPE chat_message_status AS ENUM ('sent', 'delivered', 'read');

-- Remover tabelas existentes (se necessário) - ordem inversa das dependências
DROP TABLE IF EXISTS timesheet_entries CASCADE;
DROP TABLE IF EXISTS event_participants CASCADE;
DROP TABLE IF EXISTS user_calendar_events CASCADE;
DROP TABLE IF EXISTS user_favorites CASCADE;
DROP TABLE IF EXISTS user_notifications CASCADE;
DROP TABLE IF EXISTS message_reactions CASCADE;
DROP TABLE IF EXISTS message_read_status CASCADE;
DROP TABLE IF EXISTS message_mentions CASCADE;
DROP TABLE IF EXISTS chat_messages CASCADE;
DROP TABLE IF EXISTS knowledge_article_tags CASCADE;
DROP TABLE IF EXISTS knowledge_tags CASCADE;
DROP TABLE IF EXISTS knowledge_related_tickets CASCADE;
DROP TABLE IF EXISTS project_task_dependencies CASCADE;
DROP TABLE IF EXISTS project_tasks CASCADE;
DROP TABLE IF EXISTS project_members CASCADE;
DROP TABLE IF EXISTS remote_access CASCADE;
DROP TABLE IF EXISTS company_contacts CASCADE;
DROP TABLE IF EXISTS company_systems CASCADE;
DROP TABLE IF EXISTS ticket_comments CASCADE;
DROP TABLE IF EXISTS attachments CASCADE;
DROP TABLE IF EXISTS audit_logs CASCADE;
DROP TABLE IF EXISTS payments CASCADE;
DROP TABLE IF EXISTS invoice_items CASCADE;
DROP TABLE IF EXISTS invoices CASCADE;
DROP TABLE IF EXISTS system_versions CASCADE;
DROP TABLE IF EXISTS systems CASCADE;
DROP TABLE IF EXISTS knowledge_base CASCADE;
DROP TABLE IF EXISTS projects CASCADE;
DROP TABLE IF EXISTS tickets CASCADE;
DROP TABLE IF EXISTS users CASCADE;
DROP TABLE IF EXISTS companies CASCADE;

-- Criar tabelas principais

CREATE TABLE companies (
    id UUID PRIMARY KEY DEFAULT uuid_generate_v4(),
    corporate_name VARCHAR(255) NOT NULL,
    trade_name VARCHAR(255),
    cnpj VARCHAR(20) NOT NULL UNIQUE,
    status company_status NOT NULL DEFAULT 'active',
    address JSONB,
    plan JSONB,
    financial JSONB,
    sla_config JSONB,
    created_by UUID,
    created_date TIMESTAMP NOT NULL DEFAULT CURRENT_TIMESTAMP,
    updated_date TIMESTAMP NOT NULL DEFAULT CURRENT_TIMESTAMP
);

CREATE TABLE users (
    id UUID PRIMARY KEY DEFAULT uuid_generate_v4(),
    email VARCHAR(255) NOT NULL UNIQUE,
    full_name VARCHAR(255) NOT NULL,
    role user_role NOT NULL DEFAULT 'client',
    password_hash VARCHAR(255),
    company_id UUID REFERENCES companies(id) ON DELETE SET NULL,
    avatar_url VARCHAR(255),
    phone VARCHAR(50),
    position VARCHAR(100),
    department VARCHAR(100),
    status user_status NOT NULL DEFAULT 'active',
    last_login TIMESTAMP,
    preferences JSONB,
    created_date TIMESTAMP NOT NULL DEFAULT CURRENT_TIMESTAMP,
    updated_date TIMESTAMP NOT NULL DEFAULT CURRENT_TIMESTAMP
);

-- Adicionar foreign key de companies para users após criação da tabela users
ALTER TABLE companies 
    ADD CONSTRAINT fk_companies_created_by 
    FOREIGN KEY (created_by) 
    REFERENCES users(id) 
    ON DELETE SET NULL;

CREATE TABLE tickets (
    id UUID PRIMARY KEY DEFAULT uuid_generate_v4(),
    title VARCHAR(255) NOT NULL,
    description TEXT NOT NULL,
    type ticket_type NOT NULL,
    status ticket_status NOT NULL DEFAULT 'open',
    priority ticket_priority NOT NULL DEFAULT 'medium',
    category VARCHAR(100),
    subcategory VARCHAR(100),
    company_id UUID NOT NULL REFERENCES companies(id) ON DELETE CASCADE,
    system_id UUID,
    version_id UUID,
    requester_id UUID NOT NULL REFERENCES users(id),
    assigned_to UUID REFERENCES users(id),
    due_date TIMESTAMP,
    sla_breach BOOLEAN NOT NULL DEFAULT FALSE,
    resolution_notes TEXT,
    resolution_date TIMESTAMP,
    created_date TIMESTAMP NOT NULL DEFAULT CURRENT_TIMESTAMP,
    updated_date TIMESTAMP NOT NULL DEFAULT CURRENT_TIMESTAMP
);

CREATE TABLE ticket_comments (
    id UUID PRIMARY KEY DEFAULT uuid_generate_v4(),
    ticket_id UUID NOT NULL REFERENCES tickets(id) ON DELETE CASCADE,
    user_id UUID NOT NULL REFERENCES users(id),
    content TEXT NOT NULL,
    type VARCHAR(50) NOT NULL DEFAULT 'comment',
    old_value VARCHAR(255),
    new_value VARCHAR(255),
    is_private BOOLEAN NOT NULL DEFAULT FALSE,
    created_date TIMESTAMP NOT NULL DEFAULT CURRENT_TIMESTAMP,
    updated_date TIMESTAMP NOT NULL DEFAULT CURRENT_TIMESTAMP
);

CREATE TABLE attachments (
    id UUID PRIMARY KEY DEFAULT uuid_generate_v4(),
    name VARCHAR(255) NOT NULL,
    url VARCHAR(255) NOT NULL,
    size INTEGER NOT NULL,
    type VARCHAR(100) NOT NULL,
    entity_type VARCHAR(50) NOT NULL,
    entity_id UUID NOT NULL,
    uploaded_by UUID NOT NULL REFERENCES users(id),
    uploaded_at TIMESTAMP NOT NULL DEFAULT CURRENT_TIMESTAMP
);

CREATE TABLE company_contacts (
    id UUID PRIMARY KEY DEFAULT uuid_generate_v4(),
    company_id UUID NOT NULL REFERENCES companies(id) ON DELETE CASCADE,
    name VARCHAR(255) NOT NULL,
    role VARCHAR(100),
    email VARCHAR(255),
    phone VARCHAR(50),
    is_primary BOOLEAN NOT NULL DEFAULT FALSE,
    created_date TIMESTAMP NOT NULL DEFAULT CURRENT_TIMESTAMP,
    updated_date TIMESTAMP NOT NULL DEFAULT CURRENT_TIMESTAMP
);

CREATE TABLE remote_access (
    id UUID PRIMARY KEY DEFAULT uuid_generate_v4(),
    company_id UUID NOT NULL REFERENCES companies(id) ON DELETE CASCADE,
    type remote_access_type NOT NULL,
    identifier VARCHAR(255) NOT NULL,
    password VARCHAR(255),
    notes TEXT,
    created_by UUID REFERENCES users(id),
    created_date TIMESTAMP NOT NULL DEFAULT CURRENT_TIMESTAMP,
    updated_date TIMESTAMP NOT NULL DEFAULT CURRENT_TIMESTAMP
);

CREATE TABLE company_systems (
    id UUID PRIMARY KEY DEFAULT uuid_generate_v4(),
    company_id UUID NOT NULL REFERENCES companies(id) ON DELETE CASCADE,
    system_id UUID NOT NULL REFERENCES systems(id) ON DELETE CASCADE,
    version_id UUID REFERENCES system_versions(id),
    installation_date DATE NOT NULL,
    status VARCHAR(50) NOT NULL DEFAULT 'active',
    license_info JSONB,
    access_info JSONB,
    custom_settings JSONB,
    maintenance_history JSONB,
    created_by UUID REFERENCES users(id),
    created_date TIMESTAMP NOT NULL DEFAULT CURRENT_TIMESTAMP,
    updated_date TIMESTAMP NOT NULL DEFAULT CURRENT_TIMESTAMP,
    UNIQUE(company_id, system_id)
);

CREATE TABLE projects (
    id UUID PRIMARY KEY DEFAULT uuid_generate_v4(),
    name VARCHAR(255) NOT NULL,
    description TEXT,
    client_id UUID NOT NULL REFERENCES companies(id) ON DELETE CASCADE,
    status project_status NOT NULL DEFAULT 'planning',
    start_date DATE NOT NULL,
    end_date DATE,
    manager UUID REFERENCES users(id),
    team JSONB,
    created_date TIMESTAMP NOT NULL DEFAULT CURRENT_TIMESTAMP,
    updated_date TIMESTAMP NOT NULL DEFAULT CURRENT_TIMESTAMP
);

CREATE TABLE project_members (
    project_id UUID NOT NULL REFERENCES projects(id) ON DELETE CASCADE,
    user_id UUID NOT NULL REFERENCES users(id),
    role VARCHAR(100),
    added_date TIMESTAMP NOT NULL DEFAULT CURRENT_TIMESTAMP,
    PRIMARY KEY (project_id, user_id)
);

CREATE TABLE project_tasks (
    id UUID PRIMARY KEY DEFAULT uuid_generate_v4(),
    project_id UUID NOT NULL REFERENCES projects(id) ON DELETE CASCADE,
    title VARCHAR(255) NOT NULL,
    description TEXT,
    status VARCHAR(50) NOT NULL DEFAULT 'todo',
    assigned_to UUID REFERENCES users(id),
    start_date DATE,
    due_date DATE,
    estimated_hours INTEGER,
    actual_hours INTEGER,
    order_index INTEGER NOT NULL DEFAULT 0,
    created_by UUID NOT NULL REFERENCES users(id),
    created_date TIMESTAMP NOT NULL DEFAULT CURRENT_TIMESTAMP,
    updated_date TIMESTAMP NOT NULL DEFAULT CURRENT_TIMESTAMP
);

CREATE TABLE project_task_dependencies (
    task_id UUID NOT NULL REFERENCES project_tasks(id) ON DELETE CASCADE,
    dependency_id UUID NOT NULL REFERENCES project_tasks(id) ON DELETE CASCADE,
    PRIMARY KEY (task_id, dependency_id)
);

CREATE TABLE knowledge_base (
    id UUID PRIMARY KEY DEFAULT uuid_generate_v4(),
    title VARCHAR(255) NOT NULL,
    content TEXT NOT NULL,
    category VARCHAR(100) NOT NULL,
    tags TEXT[],
    related_tickets UUID[],
    created_by UUID REFERENCES users(id),
    created_date TIMESTAMP NOT NULL DEFAULT CURRENT_TIMESTAMP,
    updated_date TIMESTAMP NOT NULL DEFAULT CURRENT_TIMESTAMP
);

CREATE TABLE knowledge_tags (
    id UUID PRIMARY KEY DEFAULT uuid_generate_v4(),
    name VARCHAR(100) NOT NULL UNIQUE,
    created_date TIMESTAMP NOT NULL DEFAULT CURRENT_TIMESTAMP
);

CREATE TABLE knowledge_article_tags (
    article_id UUID NOT NULL REFERENCES knowledge_base(id) ON DELETE CASCADE,
    tag_id UUID NOT NULL REFERENCES knowledge_tags(id) ON DELETE CASCADE,
    PRIMARY KEY (article_id, tag_id)
);

CREATE TABLE knowledge_related_tickets (
    article_id UUID NOT NULL REFERENCES knowledge_base(id) ON DELETE CASCADE,
    ticket_id UUID NOT NULL REFERENCES tickets(id) ON DELETE CASCADE,
    PRIMARY KEY (article_id, ticket_id)
);

CREATE TABLE systems (
    id UUID PRIMARY KEY DEFAULT uuid_generate_v4(),
    name VARCHAR(255) NOT NULL,
    description TEXT,
    category system_category NOT NULL,
    active BOOLEAN NOT NULL DEFAULT TRUE,
    logo_url VARCHAR(255),
    company_id UUID REFERENCES companies(id) ON DELETE SET NULL,
    default_sla JSONB,
    documentation_url VARCHAR(255),
    created_by UUID REFERENCES users(id),
    created_date TIMESTAMP NOT NULL DEFAULT CURRENT_TIMESTAMP,
    updated_date TIMESTAMP NOT NULL DEFAULT CURRENT_TIMESTAMP
);

CREATE TABLE system_versions (
    id UUID PRIMARY KEY DEFAULT uuid_generate_v4(),
    system_id UUID NOT NULL REFERENCES systems(id) ON DELETE CASCADE,
    version VARCHAR(50) NOT NULL,
    release_date DATE NOT NULL,
    status version_status NOT NULL DEFAULT 'stable',
    changelog TEXT,
    supported_until DATE,
    is_lts BOOLEAN NOT NULL DEFAULT FALSE,
    created_date TIMESTAMP NOT NULL DEFAULT CURRENT_TIMESTAMP,
    updated_date TIMESTAMP NOT NULL DEFAULT CURRENT_TIMESTAMP
);

CREATE TABLE invoices (
    id UUID PRIMARY KEY DEFAULT uuid_generate_v4(),
    company_id UUID NOT NULL REFERENCES companies(id) ON DELETE CASCADE,
    number VARCHAR(50) NOT NULL,
    amount DECIMAL(10,2) NOT NULL,
    due_date DATE NOT NULL,
    status invoice_status NOT NULL DEFAULT 'pending',
    payment_method payment_method,
    payment_details JSONB,
    items JSONB,
    payment_date TIMESTAMP,
    notes TEXT,
    created_date TIMESTAMP NOT NULL DEFAULT CURRENT_TIMESTAMP,
    updated_date TIMESTAMP NOT NULL DEFAULT CURRENT_TIMESTAMP
);

CREATE TABLE invoice_items (
    id UUID PRIMARY KEY DEFAULT uuid_generate_v4(),
    invoice_id UUID NOT NULL REFERENCES invoices(id) ON DELETE CASCADE,
    description TEXT NOT NULL,
    quantity INTEGER NOT NULL,
    unit_price DECIMAL(10,2) NOT NULL,
    total DECIMAL(10,2) NOT NULL
);

CREATE TABLE payments (
    id UUID PRIMARY KEY DEFAULT uuid_generate_v4(),
    invoice_id UUID NOT NULL REFERENCES invoices(id) ON DELETE CASCADE,
    company_id UUID NOT NULL REFERENCES companies(id) ON DELETE CASCADE,
    amount DECIMAL(10,2) NOT NULL,
    payment_date TIMESTAMP NOT NULL,
    payment_method payment_method NOT NULL,
    status payment_status NOT NULL DEFAULT 'pending',
    transaction_id VARCHAR(100),
    payment_proof JSONB,
    created_date TIMESTAMP NOT NULL DEFAULT CURRENT_TIMESTAMP,
    updated_date TIMESTAMP NOT NULL DEFAULT CURRENT_TIMESTAMP
);

CREATE TABLE audit_logs (
    id UUID PRIMARY KEY DEFAULT uuid_generate_v4(),
    user_id UUID REFERENCES users(id),
    action VARCHAR(50) NOT NULL,
    entity_type VARCHAR(50) NOT NULL,
    entity_id UUID NOT NULL,
    old_value JSONB,
    new_value JSONB,
    ip_address VARCHAR(45),
    user_agent TEXT,
    created_date TIMESTAMP NOT NULL DEFAULT CURRENT_TIMESTAMP
);

CREATE TABLE chat_messages (
    id UUID PRIMARY KEY DEFAULT uuid_generate_v4(),
    sender_id UUID NOT NULL REFERENCES users(id),
    ticket_id UUID REFERENCES tickets(id) ON DELETE CASCADE,
    receiver_id UUID REFERENCES users(id),
    department VARCHAR(50),
    content TEXT NOT NULL,
    type chat_message_type NOT NULL DEFAULT 'text',
    is_private BOOLEAN NOT NULL DEFAULT FALSE,
    is_official BOOLEAN NOT NULL DEFAULT FALSE,
    mentions UUID[],
    reply_to UUID REFERENCES chat_messages(id),
    attachments JSONB,
    reactions JSONB,
    read_by UUID[],
    status chat_message_status NOT NULL DEFAULT 'sent',
    created_date TIMESTAMP NOT NULL DEFAULT CURRENT_TIMESTAMP,
    updated_date TIMESTAMP NOT NULL DEFAULT CURRENT_TIMESTAMP
);

CREATE TABLE message_read_status (
    message_id UUID NOT NULL REFERENCES chat_messages(id) ON DELETE CASCADE,
    user_id UUID NOT NULL REFERENCES users(id),
    read_at TIMESTAMP NOT NULL DEFAULT CURRENT_TIMESTAMP,
    PRIMARY KEY (message_id, user_id)
);

CREATE TABLE message_reactions (
    message_id UUID NOT NULL REFERENCES chat_messages(id) ON DELETE CASCADE,
    user_id UUID NOT NULL REFERENCES users(id),
    emoji VARCHAR(50) NOT NULL,
    created_date TIMESTAMP NOT NULL DEFAULT CURRENT_TIMESTAMP,
    PRIMARY KEY (message_id, user_id, emoji)
);

CREATE TABLE message_mentions (
    message_id UUID NOT NULL REFERENCES chat_messages(id) ON DELETE CASCADE,
    user_id UUID NOT NULL REFERENCES users(id),
    created_date TIMESTAMP NOT NULL DEFAULT CURRENT_TIMESTAMP,
    PRIMARY KEY (message_id, user_id)
);

CREATE TABLE user_notifications (
    id UUID PRIMARY KEY DEFAULT uuid_generate_v4(),
    user_id UUID NOT NULL REFERENCES users(id) ON DELETE CASCADE,
    title VARCHAR(255) NOT NULL,
    description TEXT NOT NULL,
    type VARCHAR(50) NOT NULL,
    related_id UUID,
    action_url VARCHAR(255),
    read BOOLEAN NOT NULL DEFAULT FALSE,
    priority VARCHAR(50) NOT NULL DEFAULT 'medium',
    created_date TIMESTAMP NOT NULL DEFAULT CURRENT_TIMESTAMP
);

CREATE TABLE user_favorites (
    id UUID PRIMARY KEY DEFAULT uuid_generate_v4(),
    user_id UUID NOT NULL REFERENCES users(id) ON DELETE CASCADE,
    item_type VARCHAR(50) NOT NULL,
    item_id UUID NOT NULL,
    name VARCHAR(255) NOT NULL,
    description TEXT,
    icon VARCHAR(50),
    created_date TIMESTAMP NOT NULL DEFAULT CURRENT_TIMESTAMP,
    UNIQUE(user_id, item_type, item_id)
);

CREATE TABLE user_calendar_events (
    id UUID PRIMARY KEY DEFAULT uuid_generate_v4(),
    user_id UUID NOT NULL REFERENCES users(id) ON DELETE CASCADE,
    title VARCHAR(255) NOT NULL,
    description TEXT,
    start_date TIMESTAMP NOT NULL,
    end_date TIMESTAMP NOT NULL,
    all_day BOOLEAN NOT NULL DEFAULT FALSE,
    color VARCHAR(50),
    related_type VARCHAR(50),
    related_id UUID,
    status VARCHAR(50) NOT NULL DEFAULT 'scheduled',
    notification BOOLEAN NOT NULL DEFAULT TRUE,
    notification_time INTEGER NOT NULL DEFAULT 15,
    location VARCHAR(255),
    created_date TIMESTAMP NOT NULL DEFAULT CURRENT_TIMESTAMP,
    updated_date TIMESTAMP NOT NULL DEFAULT CURRENT_TIMESTAMP
);

CREATE TABLE event_participants (
    event_id UUID NOT NULL REFERENCES user_calendar_events(id) ON DELETE CASCADE,
    user_id UUID NOT NULL REFERENCES users(id),
    status VARCHAR(50) NOT NULL DEFAULT 'invited',
    response_date TIMESTAMP,
    created_date TIMESTAMP NOT NULL DEFAULT CURRENT_TIMESTAMP,
    PRIMARY KEY (event_id, user_id)
);

CREATE TABLE timesheet_entries (
    id UUID PRIMARY KEY DEFAULT uuid_generate_v4(),
    user_id UUID NOT NULL REFERENCES users(id) ON DELETE CASCADE,
    date DATE NOT NULL,
    start_time TIME,
    end_time TIME,
    duration INTEGER NOT NULL,
    activity_type VARCHAR(50) NOT NULL,
    related_id UUID,
    description TEXT NOT NULL,
    billable BOOLEAN NOT NULL DEFAULT TRUE,
    status VARCHAR(50) NOT NULL DEFAULT 'draft',
    reviewer_id UUID REFERENCES users(id),
    review_date TIMESTAMP,
    review_notes TEXT,
    created_date TIMESTAMP NOT NULL DEFAULT CURRENT_TIMESTAMP,
    updated_date TIMESTAMP NOT NULL DEFAULT CURRENT_TIMESTAMP
);

-- Criar índices para melhor performance
CREATE INDEX idx_tickets_company ON tickets(company_id);
CREATE INDEX idx_tickets_requester ON tickets(requester_id);
CREATE INDEX idx_tickets_assigned ON tickets(assigned_to);
CREATE INDEX idx_projects_client ON projects(client_id);
CREATE INDEX idx_invoices_company ON invoices(company_id);
CREATE INDEX idx_payments_invoice ON payments(invoice_id);
CREATE INDEX idx_payments_company ON payments(company_id);
CREATE INDEX idx_chat_messages_ticket ON chat_messages(ticket_id);
CREATE INDEX idx_chat_messages_sender ON chat_messages(sender_id);
CREATE INDEX idx_chat_messages_receiver ON chat_messages(receiver_id);
CREATE INDEX idx_ticket_comments_ticket ON ticket_comments(ticket_id);
CREATE INDEX idx_attachments_entity ON attachments(entity_type, entity_id);
CREATE INDEX idx_company_contacts_company ON company_contacts(company_id);
CREATE INDEX idx_remote_access_company ON remote_access(company_id);
CREATE INDEX idx_company_systems_company ON company_systems(company_id);
CREATE INDEX idx_company_systems_system ON company_systems(system_id);
CREATE INDEX idx_project_tasks_project ON project_tasks(project_id);
CREATE INDEX idx_project_tasks_assigned ON project_tasks(assigned_to);
CREATE INDEX idx_knowledge_article_tags_article ON knowledge_article_tags(article_id);
CREATE INDEX idx_message_read_status_message ON message_read_status(message_id);
CREATE INDEX idx_message_reactions_message ON message_reactions(message_id);
CREATE INDEX idx_user_notifications_user ON user_notifications(user_id);
CREATE INDEX idx_user_favorites_user ON user_favorites(user_id);
CREATE INDEX idx_calendar_events_user ON user_calendar_events(user_id);
CREATE INDEX idx_timesheet_entries_user ON timesheet_entries(user_id);
CREATE INDEX idx_timesheet_entries_date ON timesheet_entries(date);

-- Criar funções para atualização automática de updated_date
CREATE OR REPLACE FUNCTION update_updated_date()
RETURNS TRIGGER AS $$
BEGIN
    NEW.updated_date = CURRENT_TIMESTAMP;
    RETURN NEW;
END;
$$ language 'plpgsql';

-- Criar triggers para atualização automática de updated_date
CREATE TRIGGER update_users_updated_date
    BEFORE UPDATE ON users
    FOR EACH ROW
    EXECUTE FUNCTION update_updated_date();

CREATE TRIGGER update_companies_updated_date
    BEFORE UPDATE ON companies
    FOR EACH ROW
    EXECUTE FUNCTION update_updated_date();

CREATE TRIGGER update_tickets_updated_date
    BEFORE UPDATE ON tickets
    FOR EACH ROW
    EXECUTE FUNCTION update_updated_date();

CREATE TRIGGER update_projects_updated_date
    BEFORE UPDATE ON projects
    FOR EACH ROW
    EXECUTE FUNCTION update_updated_date();

CREATE TRIGGER update_knowledge_base_updated_date
    BEFORE UPDATE ON knowledge_base
    FOR EACH ROW
    EXECUTE FUNCTION update_updated_date();

CREATE TRIGGER update_systems_updated_date
    BEFORE UPDATE ON systems
    FOR EACH ROW
    EXECUTE FUNCTION update_updated_date();

CREATE TRIGGER update_system_versions_updated_date
    BEFORE UPDATE ON system_versions
    FOR EACH ROW
    EXECUTE FUNCTION update_updated_date();

CREATE TRIGGER update_invoices_updated_date
    BEFORE UPDATE ON invoices
    FOR EACH ROW
    EXECUTE FUNCTION update_updated_date();

CREATE TRIGGER update_payments_updated_date
    BEFORE UPDATE ON payments
    FOR EACH ROW
    EXECUTE FUNCTION update_updated_date();

CREATE TRIGGER update_chat_messages_updated_date
    BEFORE UPDATE ON chat_messages
    FOR EACH ROW
    EXECUTE FUNCTION update_updated_date();

CREATE TRIGGER update_ticket_comments_updated_date
    BEFORE UPDATE ON ticket_comments
    FOR EACH ROW
    EXECUTE FUNCTION update_updated_date();

CREATE TRIGGER update_company_contacts_updated_date
    BEFORE UPDATE ON company_contacts
    FOR EACH ROW
    EXECUTE FUNCTION update_updated_date();

CREATE TRIGGER update_remote_access_updated_date
    BEFORE UPDATE ON remote_access
    FOR EACH ROW
    EXECUTE FUNCTION update_updated_date();

CREATE TRIGGER update_company_systems_updated_date
    BEFORE UPDATE ON company_systems
    FOR EACH ROW
    EXECUTE FUNCTION update_updated_date();

CREATE TRIGGER update_project_tasks_updated_date
    BEFORE UPDATE ON project_tasks
    FOR EACH ROW
    EXECUTE FUNCTION update_updated_date();

CREATE TRIGGER update_timesheet_entries_updated_date
    BEFORE UPDATE ON timesheet_entries
    FOR EACH ROW
    EXECUTE FUNCTION update_updated_date();

-- Comentários nas tabelas principais
COMMENT ON TABLE users IS 'Armazena informações dos usuários do sistema';
COMMENT ON TABLE companies IS 'Armazena informações das empresas clientes';
COMMENT ON TABLE tickets IS 'Armazena os chamados de suporte';
COMMENT ON TABLE projects IS 'Armazena os projetos';
COMMENT ON TABLE knowledge_base IS 'Base de conhecimento do sistema';
COMMENT ON TABLE systems IS 'Sistemas suportados pela empresa';
COMMENT ON TABLE invoices IS 'Faturas emitidas para os clientes';
COMMENT ON TABLE payments IS 'Registros de pagamentos das faturas';
COMMENT ON TABLE audit_logs IS 'Log de auditoria do sistema';
COMMENT ON TABLE chat_messages IS 'Mensagens do chat interno e com clientes';
COMMENT ON TABLE ticket_comments IS 'Comentários e histórico de alterações dos chamados';
COMMENT ON TABLE attachments IS 'Arquivos anexados a chamados, mensagens e outros itens';
COMMENT ON TABLE company_contacts IS 'Contatos das empresas clientes';
COMMENT ON TABLE remote_access IS 'Informações de acesso remoto das empresas';
COMMENT ON TABLE company_systems IS 'Sistemas utilizados por cada empresa';
COMMENT ON TABLE project_members IS 'Membros da equipe de cada projeto';
COMMENT ON TABLE project_tasks IS 'Tarefas dos projetos';
COMMENT ON TABLE knowledge_tags IS 'Tags para classificação de artigos';
COMMENT ON TABLE user_notifications IS 'Notificações do sistema para os usuários';
COMMENT ON TABLE user_favorites IS 'Itens favoritados pelos usuários';
COMMENT ON TABLE user_calendar_events IS 'Eventos do calendário dos usuários';
COMMENT ON TABLE timesheet_entries IS 'Registros de horas trabalhadas';

-- Configurar permissões
GRANT ALL PRIVILEGES ON ALL TABLES IN SCHEMA public TO CURRENT_USER;
GRANT USAGE, SELECT ON ALL SEQUENCES IN SCHEMA public TO CURRENT_USER;

-- Fim do script
`;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Database className="h-5 w-5 text-primary" />
            <CardTitle>Script PostgreSQL</CardTitle>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleCopyToClipboard}
            >
              {copied ? (
                <Check className="h-4 w-4 mr-2" />
              ) : (
                <Copy className="h-4 w-4 mr-2" />
              )}
              {copied ? 'Copiado!' : 'Copiar'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownload}
            >
              <Download className="h-4 w-4 mr-2" />
              Download
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="mb-4 p-4 bg-yellow-50 rounded-md">
          <h3 className="font-medium mb-2">Instruções de execução:</h3>
          <ol className="list-decimal ml-4 space-y-1">
            <li>Primeiro, crie o banco de dados: <code>CREATE DATABASE helpdesk_pro;</code></li>
            <li>Conecte ao banco de dados: <code>\\c helpdesk_pro</code></li>
            <li>Execute o script SQL completo</li>
          </ol>
        </div>
        <ScrollArea className="h-[500px] w-full rounded-md border p-4">
          <pre className="text-sm">
            <code>{sqlScript}</code>
          </pre>
        </ScrollArea>
      </CardContent>
    </Card>
  );
}
